import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import { exercisesApi } from '@/lib/api';
import type { Exercise, MuscleGroup } from '@/types';
import { MUSCLE_GROUPS, MUSCLE_GROUP_LABELS } from '@/types';
import { PlusIcon, TrashIcon } from '@/components/Icons';

// Muscle group icon paths
const MUSCLE_ICON_PATHS: Record<string, string> = {
  chest: '/icons/muscles/chest.png',
  back: '/icons/muscles/back.png',
  shoulders: '/icons/muscles/shoulders.png',
  biceps: '/icons/muscles/biceps.png',
  triceps: '/icons/muscles/triceps.png',
  quads: '/icons/muscles/quads.png',
  hamstrings: '/icons/muscles/hamstrings.png',
  glutes: '/icons/muscles/glutes.png',
  calves: '/icons/muscles/calves.png',
  abs: '/icons/muscles/abs.png',
  forearms: '/icons/muscles/forearms.png',
};
import LoadingSpinner from '@/components/LoadingSpinner';
import Modal from '@/components/Modal';

export default function ExercisesPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [musclePrimary, setMusclePrimary] = useState<MuscleGroup>('chest');
  const [muscleSecondary, setMuscleSecondary] = useState<MuscleGroup | ''>('');
  const [technique, setTechnique] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState(false); // For viewing technique only

  const { data: exercises = [], isLoading: loading } = useQuery({
    queryKey: ['exercises'],
    queryFn: async () => {
      const res = await exercisesApi.list(true);
      return res.success ? res.data : [];
    },
  });

  const refetchExercises = () => {
    queryClient.invalidateQueries({ queryKey: ['exercises'] });
  };

  const openAddModal = () => {
    setEditingExercise(null);
    setName('');
    setMusclePrimary('chest');
    setMuscleSecondary('');
    setTechnique('');
    setError('');
    setViewMode(false);
    setShowModal(true);
  };

  const openEditModal = (exercise: Exercise) => {
    setEditingExercise(exercise);
    setName(exercise.name);
    setMusclePrimary(exercise.muscle_primary);
    setMuscleSecondary(exercise.muscle_secondary || '');
    setTechnique(exercise.technique || '');
    setError('');
    setViewMode(true); // Always open in view mode first
    setShowModal(true);
  };

  const switchToEditMode = () => {
    setViewMode(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    setError('');

    const data = {
      name: name.trim(),
      muscle_primary: musclePrimary,
      muscle_secondary: muscleSecondary || undefined,
      technique: technique.trim() || undefined,
    };

    const res = editingExercise
      ? await exercisesApi.update(editingExercise.id, data)
      : await exercisesApi.create(data);

    if (res.success) {
      refetchExercises();
      setShowModal(false);
      showToast(editingExercise ? 'Упражнение обновлено' : 'Упражнение добавлено', 'success');
    } else {
      setError(res.error || 'Не удалось сохранить упражнение');
    }

    setSaving(false);
  };

  const handleDelete = async (exercise: Exercise) => {
    if (!confirm(`Удалить "${exercise.name}"? Это действие нельзя отменить.`)) return;

    const res = await exercisesApi.delete(exercise.id);
    if (res.success) {
      refetchExercises();
      setShowModal(false);
      showToast('Упражнение удалено', 'success');
    } else {
      showToast(res.error || 'Не удалось удалить упражнение', 'error');
    }
  };

  // Filter exercises by search query
  const filteredExercises = exercises.filter((e) =>
    e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    MUSCLE_GROUP_LABELS[e.muscle_primary].toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group filtered exercises by muscle group
  const groupedExercises = filteredExercises.reduce<Record<MuscleGroup, Exercise[]>>(
    (acc, exercise) => {
      const group = exercise.muscle_primary;
      if (!acc[group]) acc[group] = [];
      acc[group].push(exercise);
      return acc;
    },
    {} as Record<MuscleGroup, Exercise[]>
  );

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Fixed Header with search */}
      <header className="fixed top-0 left-0 right-0 z-20 bg-white border-b border-gray-100 px-4 py-4 md:left-64">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <svg 
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск упражнений..."
              className="w-full pl-9 pr-4 py-2 bg-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all"
            />
          </div>
          <button 
            onClick={openAddModal} 
            className="w-9 h-9 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 transition-colors shadow-sm flex-shrink-0"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="p-4 space-y-6 pt-24">
        {exercises.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <p className="mb-4">Упражнений пока нет</p>
              <button onClick={openAddModal} className="btn-primary">
                Добавить первое упражнение
              </button>
            </div>
          </div>
        ) : (
          MUSCLE_GROUPS.filter((group) => groupedExercises[group]?.length > 0).map(
            (group) => (
              <section key={group}>
                <h2 className="section-title">{MUSCLE_GROUP_LABELS[group]}</h2>
                <div className="card divide-y divide-gray-100">
                  {groupedExercises[group].map((exercise) => (
                    <div
                      key={exercise.id}
                      className="flex items-center justify-between p-3"
                    >
                      <button
                        onClick={() => openEditModal(exercise)}
                        className="flex-1 text-left"
                      >
                        <p className="font-medium text-gray-900">
                          {exercise.name}
                        </p>
                        {exercise.muscle_secondary && (
                          <p className="text-sm text-gray-500">
                            Также: {MUSCLE_GROUP_LABELS[exercise.muscle_secondary]}
                          </p>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )
          )
        )}
      </div>

      {/* Add/Edit/View Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={viewMode ? '' : (editingExercise ? 'Редактировать' : 'Новое упражнение')}
        size="lg"
        hideHeader={viewMode}
      >
        {viewMode ? (
          /* View mode */
          <div className="space-y-5">
            {/* Exercise Name */}
            <div className="text-center pt-2">
              <h2 className="text-xl font-bold text-gray-900">{name}</h2>
            </div>

            {/* Muscle Groups with Icons */}
            <div className="flex justify-center gap-6">
              {/* Primary muscle */}
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center mb-2 p-3">
                  {MUSCLE_ICON_PATHS[musclePrimary] && (
                    <img 
                      src={MUSCLE_ICON_PATHS[musclePrimary]} 
                      alt={MUSCLE_GROUP_LABELS[musclePrimary]}
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">{MUSCLE_GROUP_LABELS[musclePrimary]}</span>
                <span className="text-xs text-primary-600">основная</span>
              </div>

              {/* Secondary muscle */}
              {muscleSecondary && (
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-2 p-3">
                    {MUSCLE_ICON_PATHS[muscleSecondary] && (
                      <img 
                        src={MUSCLE_ICON_PATHS[muscleSecondary]} 
                        alt={MUSCLE_GROUP_LABELS[muscleSecondary as MuscleGroup]}
                        className="w-full h-full object-contain opacity-70"
                      />
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700">{MUSCLE_GROUP_LABELS[muscleSecondary as MuscleGroup]}</span>
                  <span className="text-xs text-gray-400">дополнительная</span>
                </div>
              )}
            </div>

            {/* Technique */}
            {technique ? (
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Техника выполнения</h3>
                <div className="p-4 bg-gray-50 rounded-xl text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                  {technique}
                </div>
              </div>
            ) : (
              <div className="p-6 bg-gray-50 rounded-xl text-gray-400 text-sm text-center">
                Техника выполнения не указана
              </div>
            )}
            
            {/* Action buttons */}
            {editingExercise && !editingExercise.is_global ? (
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Закрыть
                </button>
                <button
                  onClick={switchToEditMode}
                  className="btn-primary flex-1"
                >
                  Редактировать
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowModal(false)}
                className="btn-primary w-full"
              >
                Закрыть
              </button>
            )}
          </div>
        ) : (
          /* Edit/Create form */
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="name" className="label">
                Название
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="напр., Жим лёжа"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="muscle_primary" className="label">
                  Основная мышца
                </label>
                <select
                  id="muscle_primary"
                  value={musclePrimary}
                  onChange={(e) => setMusclePrimary(e.target.value as MuscleGroup)}
                  className="input"
                >
                  {MUSCLE_GROUPS.map((group) => (
                    <option key={group} value={group}>
                      {MUSCLE_GROUP_LABELS[group]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="muscle_secondary" className="label">
                  Дополнительная
                </label>
                <select
                  id="muscle_secondary"
                  value={muscleSecondary}
                  onChange={(e) =>
                    setMuscleSecondary(e.target.value as MuscleGroup | '')
                  }
                  className="input"
                >
                  <option value="">Нет</option>
                  {MUSCLE_GROUPS.filter((g) => g !== musclePrimary).map((group) => (
                    <option key={group} value={group}>
                      {MUSCLE_GROUP_LABELS[group]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="technique" className="label">
                Техника выполнения
              </label>
              <textarea
                id="technique"
                value={technique}
                onChange={(e) => setTechnique(e.target.value)}
                className="input"
                rows={4}
                placeholder="Опишите правильную технику выполнения упражнения..."
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="btn-secondary flex-1"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={saving || !name.trim()}
                className="btn-primary flex-1"
              >
                {saving ? <LoadingSpinner size="sm" className="text-white" /> : 'Сохранить'}
              </button>
            </div>

            {editingExercise && !editingExercise.is_global && (
              <button
                type="button"
                onClick={() => {
                  handleDelete(editingExercise);
                  setShowModal(false);
              }}
              className="btn-danger w-full"
            >
              <TrashIcon className="w-4 h-4 mr-2" />
              Удалить упражнение
            </button>
          )}
          </form>
        )}
      </Modal>
    </div>
  );
}
